import { useSyncExternalStore } from "react";

/**
 * Shared iOS-keyboard-inset tracking.
 *
 * On iOS (including Despia WebViews) the software keyboard shrinks only the
 * visual viewport — `position: fixed` overlays don't move, so inputs pinned
 * near the bottom of a fixed sheet/dialog get hidden behind the keyboard.
 *
 * This hook attaches a single module-level listener to
 * `window.visualViewport`, reference-counted across all subscribers, and:
 *   - writes the current keyboard inset (px) to the CSS custom property
 *     `--keyboard-inset` on `document.documentElement`
 *   - toggles a `keyboard-visible` class on `document.body`
 *   - returns the current inset to callers via `useSyncExternalStore`
 *
 * Consumers can either read the returned number or, more commonly, use the
 * CSS variable inline, e.g.:
 *   style={{ paddingBottom:
 *     'calc(env(safe-area-inset-bottom) + var(--keyboard-inset, 0px))' }}
 *
 * Rules-of-Hooks: call this hook unconditionally at the top of a component.
 * The underlying listener is shared, so calling it in many components is
 * cheap. Never place the call inside a conditional branch.
 */

let inset = 0;
const listeners = new Set<() => void>();
let refcount = 0;
let rafId: number | null = null;
let vv: VisualViewport | null = null;

function hasWindow(): boolean {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

function computeInset(): number {
  if (!hasWindow() || !vv) return 0;
  const value = window.innerHeight - vv.height - vv.offsetTop;
  return value > 0 ? value : 0;
}

function applyInset(next: number) {
  if (next === inset) return;
  inset = next;
  const root = document.documentElement;
  root.style.setProperty("--keyboard-inset", `${next}px`);
  document.body.classList.toggle("keyboard-visible", next > 0);
  for (const l of listeners) l();
}

function scheduleUpdate() {
  if (rafId !== null) return;
  rafId = requestAnimationFrame(() => {
    rafId = null;
    applyInset(computeInset());
  });
}

function attach() {
  if (!hasWindow()) return;
  vv = window.visualViewport ?? null;
  if (!vv) return;
  vv.addEventListener("resize", scheduleUpdate);
  vv.addEventListener("scroll", scheduleUpdate);
  scheduleUpdate();
}

function detach() {
  if (rafId !== null) {
    cancelAnimationFrame(rafId);
    rafId = null;
  }
  if (vv) {
    vv.removeEventListener("resize", scheduleUpdate);
    vv.removeEventListener("scroll", scheduleUpdate);
  }
  vv = null;
  // Reset published state so stale --keyboard-inset can't linger.
  if (hasWindow()) {
    document.documentElement.style.setProperty("--keyboard-inset", "0px");
    document.body.classList.remove("keyboard-visible");
  }
  inset = 0;
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  refcount += 1;
  if (refcount === 1) attach();
  return () => {
    listeners.delete(l);
    refcount -= 1;
    if (refcount === 0) detach();
  };
}

const getSnapshot = () => inset;
const getServerSnapshot = () => 0;

export function useKeyboardInset(): number {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
